import { css } from '@emotion/css';
import { Box, Checkbox, Group, Tooltip } from '@mantine/core';
import { IconCircle, IconCircleCheckFilled } from '@tabler/icons-react';
import type { Row } from '@tanstack/react-table';
import { i18nKeys, useTranslation } from 'locales';
import type { LightThread } from 'modules/Mails/hooks/use-load-mails';
import { memo } from 'react';

type MailCheckboxCellProps = {
  row: Row<LightThread>;
  isSelected: boolean;
  toggleUpdateThread: () => void;
};

export const MailCheckboxCell = memo(
  ({ row, isSelected, toggleUpdateThread }: MailCheckboxCellProps) => {
    const { t } = useTranslation();

    const { isProcessed } = row.original;

    const handleCheckboxClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.target === e.currentTarget) row.toggleSelected();
    };

    const handleProcessClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleUpdateThread();
    };

    return (
      <Group gap="lg" wrap="nowrap">
        <Box className={hitboxClass} onClick={handleCheckboxClick}>
          <Checkbox
            size="xs"
            color="blue.6"
            checked={isSelected}
            onChange={row.getToggleSelectedHandler()}
          />
        </Box>
        <Tooltip
          openDelay={500}
          label={t(
            isProcessed
              ? i18nKeys.EXTERNAL_MAIL.ACTIONS.UNPROCESS
              : i18nKeys.EXTERNAL_MAIL.ACTIONS.PROCESS,
          )}
          withArrow
        >
          <Box className={hitboxClass} onClick={handleProcessClick}>
            {isProcessed ? (
              <IconCircleCheckFilled size={20} color="var(--mantine-color-validation-4)" />
            ) : (
              <IconCircle size={20} color="var(--mantine-color-gray-4)" />
            )}
          </Box>
        </Tooltip>
      </Group>
    );
  },
  // Custom comparator because `row` is a new TanStack object reference on every
  // table render — shallow equality would always fail and memo would be useless.
  // We only care about the three fields that actually affect this cell's output.
  (prev, next) =>
    prev.row.id === next.row.id &&
    prev.isSelected === next.isSelected &&
    prev.row.original.isProcessed === next.row.original.isProcessed,
);

const hitboxClass = css({
  padding: '10px',
  margin: '-10px',
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
});
